import { z } from 'zod'

// API Types

// Role schema for RBAC
export const RoleSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  isSystem: z.preprocess(val => (val == null ? false : val), z.boolean()),
})

export type Role = z.infer<typeof RoleSchema>

export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string().nullable(),
  isActive: z.boolean(),
  language: z.string(),
  timezone: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastLoginAt: z.string().nullable(),
  // Azure fields
  azureTenantId: z.string().nullable(),
  azureObjectId: z.string().nullable(),
  // 2FA fields
  totpEnabled: z.preprocess(
    val => (val == null ? false : val),
    z.boolean().optional().default(false)
  ),
  // RBAC fields
  roles: z.array(RoleSchema).optional().default([]),
  permissions: z.array(z.string()).optional().default([]),
  isSuperAdmin: z.preprocess(
    val => (val == null ? false : val),
    z.boolean().optional().default(false)
  ),
  requires_2faSetup: z.boolean().optional().default(false),
})

export type User = z.infer<typeof UserSchema>

export const ApiErrorSchema = z.object({
  code: z.number(),
  message: z.union([z.string(), z.record(z.string(), z.any())]),
  details: z.record(z.string(), z.any()).optional(),
})

export type ApiErrorResponse = z.infer<typeof ApiErrorSchema>

export type ApiError = {
  error: string
  message: string
  status: number
  details?: Record<string, unknown>
}

// Base API Response
export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: dataSchema,
    status: z.string(),
  })

// Auth types
export const LoginDataSchema = z.object({
  user: UserSchema,
  roles: z.array(RoleSchema).optional(),
  permissions: z.array(z.string()).optional(),
  isSuperAdmin: z.preprocess(val => (val == null ? false : val), z.boolean().optional()),
  requires_2fa: z.boolean().optional(),
  requires_2faSetup: z.boolean().optional(),
  setupToken: z.string().optional(),
  // Tokens are now in HttpOnly cookies, not in response body
})

export type LoginData = z.infer<typeof LoginDataSchema>

export const LoginResponseSchema = z.object({
  code: z.number(),
  message: z.string(),
  data: LoginDataSchema,
})

export type LoginResponse = z.infer<typeof LoginResponseSchema>

// Profile response with RBAC data
export const ProfileDataSchema = z.object({
  user: UserSchema,
  roles: z.array(RoleSchema),
  permissions: z.array(z.string()),
  isSuperAdmin: z.preprocess(val => (val == null ? false : val), z.boolean()),
})

export type ProfileData = z.infer<typeof ProfileDataSchema>

export const ProfileResponseSchema = z.object({
  code: z.number(),
  message: z.string(),
  data: ProfileDataSchema,
})

export type ProfileResponse = z.infer<typeof ProfileResponseSchema>

export const AuthStateSchema = z.object({
  isAuthenticated: z.boolean(),
  user: UserSchema.nullable(),
  isLoading: z.boolean(),
  // Token is in HttpOnly cookie, not accessible to JavaScript
})

export type AuthState = z.infer<typeof AuthStateSchema>

// Audit log types
export const AuditLogSchema = z.object({
  id: z.string(),
  action: z.string(),
  description: z.string().nullable(),
  userId: z.string(),
  crisisId: z.string().nullable(),
  resourceType: z.string().nullable(),
  resourceId: z.string().nullable(),
  details: z.any().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  requestMethod: z.string().nullable(),
  requestPath: z.string().nullable(),
  createdAt: z.string(),
})

export type AuditLog = z.infer<typeof AuditLogSchema>

export const AuditLogsListResponseSchema = z.object({
  logs: z.array(AuditLogSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
})

export type AuditLogsListResponse = z.infer<typeof AuditLogsListResponseSchema>

export const AuditActionsResponseSchema = z.object({
  actions: z.array(z.string()),
})

export type AuditActionsResponse = z.infer<typeof AuditActionsResponseSchema>

// RBAC types for role and permission management
export const PermissionSchema = z.object({
  id: z.string(),
  code: z.string(),
  resource: z.string().nullable(),
  action: z.string().nullable(),
  description: z.string().nullable(),
  category: z.string().nullable(),
  isSystem: z.preprocess(val => (val == null ? false : val), z.boolean()),
  isEssential: z.preprocess(
    val => (val == null ? false : val),
    z.boolean().optional().default(false)
  ),
})

export type Permission = z.infer<typeof PermissionSchema>

export const RoleDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string(),
  isSystem: z.preprocess(val => (val == null ? false : val), z.boolean()),
  permissions: z.array(PermissionSchema),
  userCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type RoleDetail = z.infer<typeof RoleDetailSchema>

export const RoleListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string(),
  isSystem: z.preprocess(val => (val == null ? false : val), z.boolean()),
  permissionCount: z.number(),
  userCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type RoleListItem = z.infer<typeof RoleListItemSchema>

export const PermissionCategorySchema = z.object({
  category: z.string(),
  count: z.number(),
})

export type PermissionCategory = z.infer<typeof PermissionCategorySchema>
